import { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip } from "antd";
import { BsThreeDotsVertical } from "react-icons/bs";
import { HiOfficeBuilding } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import ReactFlagsSelect from "react-flags-select";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppIcon from "../../components/Common/AppIcon";
import cutomiseIcon from "../../assets/img/cutomiseIcon.svg";







import { authSelector } from "../../components/Auth/AuthSlice";
import {
  toggleBulkSMSOverlay,
  toggleQuickSMSOverlay,
} from "../../components/Message/MessageSlice";
import { getProfileData } from "../../components/Profile/ProfileSlice";
import SubmissionReport from "../../components/Report/InnerReport/Submission";
import { userListGet } from "../../components/User/UserSlice";
import useAbacAccess from "../../hooks/useAbacAccess";
import useModal from "../../hooks/useModal";
import {
  checkForWalletMin,
  getItemWithExpiry,
  hasRoles,
  isArray,
  isManager,
  isSeller,
} from "../../lib/utility";
import { middleware } from "../../redux/store";
import {
  getBalanceData,
  walletSelector,
} from "../../components/Wallet/WalletSlice";
import AddBalanceRequestModal from "./AddBalanceRequestModal";
import CustomizePanelFilter from "./CustomizePanel/CustomizePanelFilter";
import DownloadProgressModal from "./DownloadProgress/DownloadProgressModal";
import LowBalanceAlertModal from "./LowBalanceAlertModal";
import "./Navbar.scss";
import NotificationModal from "./Notifications/NotificationModal";
import {
  clearStatenotification,
  notificationSelector,
  setNotificationFetching,
  setNotificationsFromSocket,
} from "./Notifications/NotificationSlice";
// import useNotificationThrottle from "./Notifications/useNotificationThrottle";
import i18n, { LANGUAGE_LOCAL_STORAGE_KEY } from "../../i18n/config";
import { useWebSocketNotification } from "../../contexts/WebSocketNotification";
import { commonUtilitySelector } from "../../redux/commonUtilitySlice";

const BALANCE_TOOLTIP_PROPS = {
  placement: "bottom",
  getPopupContainer: () => document.body,
  overlayClassName: "navbar-balance-tooltip",
  overlayStyle: { zIndex: 10050 },
  zIndex: 10050,
};

const isBalanceFieldPresent = (value) =>
  value !== null && value !== undefined;

const formatBalanceAmount = (value) =>
  isBalanceFieldPresent(value) ? String(value) : "0";

const BalanceChip = ({ tooltip, onClick, icon, amount, currency }) => (
  <Tooltip title={tooltip} {...BALANCE_TOOLTIP_PROPS}>
    <div
      className={"navItem wallet" + (onClick ? "" : " wallet--static")}
      onClick={onClick}
    >
      <button type="button" tabIndex={onClick ? 0 : -1}>
        {icon}
        <span className="text-dark fs-16 px-1">{amount}</span>{" "}
        {currency ? (
          <span className="text-gray fs-12 wallet-currency">{currency}</span>
        ) : null}
      </button>
    </div>
  </Tooltip>
);

const Navbar = () => {
  const { t } = useTranslation("navbar");
  const [darkMode, setDarkMode] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(() => {
    const langMap = { en: "US", hi: "IN", ar: "SA", ru: "RU" };
    return langMap[i18n.language] || "US";
  });
  const [profileData, setProfileData] = useState(
    getItemWithExpiry("profileData")
  );
  const [showNotification, setShowNotification] = useState(false);
  const [showFileProgress, setShowFileProgress] = useState(false);
  const [showSubmissionReport, setShowSubmissionReport] = useState(false);
  const [lowBalanceAlert, setLowBalanceAlert] = useState(false);
  const [showCustomizePanel, setShowCustomizePanel] = useState(false);
  const [notifyId, setNotifyId] = useState();
  const [addBalanceLater, setAddBalanceLater] = useState(false);
  // Always start at 0 — never seed from globalThis (stale across logout/role switch)
  const [notificationCount, setNotificationCount] = useState(0);
  const [userList, setUserList] = useState([]);
  const [addBalanceId, setAddBalanceId] = useState();
  const [balanceUser, setBalanceUser] = useState(false);
  const [balanceData, setBalanceData] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    connected: notificationSocketConnected,
    registerEventHandler,
    fetchNotifications,
    updateNotification,
    deleteNotification: deleteNotificationWs,
    deleteAllNotifications: deleteAllNotificationsWs,
    pendingMutationRef,
  } = useWebSocketNotification();
  const { user } = useSelector(authSelector);
  const {
    canAccess,
    canAccessDefault,
    canAccessNavbar,
    canAccessNavbarAction,
    canAccessWalletUi,
  } = useAbacAccess();
  const canShowCustomizeNav = canAccessNavbar("customize_panel");
  const canShowNotificationsNav = canAccessNavbar("notifications");
  const canClearAllNotifications = canAccessNavbarAction(
    "notifications",
    "clear_all"
  );
  const canDeleteNotification = canAccessNavbarAction("notifications", "delete");
  const canMarkNotificationRead = canAccessNavbarAction(
    "notifications",
    "markRead"
  );
  const canShowQuickMessageNav =
    canAccessNavbar("quick_message") &&
    canAccess(["sms", "message", "quick_send", "view"]);
  const canShowBulkMessageNav =
    canAccessNavbar("bulk_message") &&
    canAccess(["sms", "message", "bulk_campaign", "view"]);
  const canShowSubmissionReportNav = canAccessNavbar("submission_report");
  const canShowDownloadsNav = canAccessNavbar("downloads");
  const { notificationData, isFetching: isFetchingNotification } =
    useSelector(notificationSelector);
  const { balanceData: newBalanceData, isSuccessBalance } =
    useSelector(walletSelector);
  const canSeeDualBalance = hasRoles("tenant,superadmin,system", user);
  const { isMobile, isTablet } = useSelector(commonUtilitySelector);
  const isCompactNav = isMobile || isTablet;

  const isSupers = hasRoles("admin,tenant,superadmin,system,seller,manager", user);

  const languageLabels = {
    US: "English",
    IN: "Hindi",
    SA: "Arabic",
    RU: "Russian",
  };

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem("darkMode", newMode);
    applyTheme(newMode);
  };
  const applyTheme = (isDarkMode) => {
    const body = document.body;
    if (isDarkMode) {
      body.classList.add("dark-mode");
    } else {
      body.classList.remove("dark-mode");
    }
  };

  const flagToLangMap = {
    US: "en",
    IN: "hi",
    SA: "ar",
    RU: "ru",
  };

  const handleLanguageChange = (countryCode) => {
    const lang = flagToLangMap[countryCode];
    if (lang) {
      i18n.changeLanguage(lang);
      localStorage.setItem(LANGUAGE_LOCAL_STORAGE_KEY, lang);
      setSelectedFlag(countryCode);
    }
  };
  function handleQuickMessage(e) {
    e.preventDefault;
    if (!canShowQuickMessageNav) return;
    navigate("/message");
    dispatch(toggleQuickSMSOverlay(true));
  }
  function handleBulkMessage(e) {
    e.preventDefault;
    if (!canShowBulkMessageNav) return;
    navigate("/message");
    dispatch(toggleBulkSMSOverlay(true));
  }
  function handleSubmissionReportOpen(e) {
    e && e.preventDefault();
    if (!canShowSubmissionReportNav) return;
    setShowSubmissionReport(true);
  }
  function handleCustomizeFilter(e) {
    e && e.preventDefault();
    if (!canShowCustomizeNav) return;
    setShowCustomizePanel((prev) => !prev);
  }
  const handleNotificationModalOpen = () => {
    if (!canShowNotificationsNav) return;
    setShowNotification(true);
  };

  const handleClearAllNotifications = useCallback(() => {
    if (!canClearAllNotifications) return;
    dispatch(setNotificationFetching(true));
    deleteAllNotificationsWs();
  }, [canClearAllNotifications, dispatch, deleteAllNotificationsWs]);

  const handleFileProgressModalOpen = () => {
    if (!canShowDownloadsNav) return;
    setShowFileProgress(true);
  };

  useEffect(() => {
    if (!canShowNotificationsNav) setShowNotification(false);
  }, [canShowNotificationsNav]);

  useEffect(() => {
    if (!canShowDownloadsNav) setShowFileProgress(false);
  }, [canShowDownloadsNav]);

  useEffect(() => {
    if (!canShowCustomizeNav) setShowCustomizePanel(false);
  }, [canShowCustomizeNav]);

  useEffect(() => {
    if (!canShowSubmissionReportNav) setShowSubmissionReport(false);
  }, [canShowSubmissionReportNav]);

  useEffect(() => {
    if (!canShowQuickMessageNav) dispatch(toggleQuickSMSOverlay(false));
  }, [canShowQuickMessageNav, dispatch]);

  useEffect(() => {
    if (!canShowBulkMessageNav) dispatch(toggleBulkSMSOverlay(false));
  }, [canShowBulkMessageNav, dispatch]);
  const getNotificationDataOnce = useCallback(
    (force) => {
      if (!notificationSocketConnected) return;
      dispatch(setNotificationFetching(true));
      fetchNotifications();
    },
    [dispatch, fetchNotifications, notificationSocketConnected]
  );
  const handleNotificationClick = useCallback(
    (id) => {
      if (!canMarkNotificationRead) return;
      if (!id)
        return console.error("Id is required to mark notification read");
      updateNotification(id, "yes", "no");
    },
    [canMarkNotificationRead, updateNotification]
  );
  const deleteNotificationOnce = useCallback(
    (id) => {
      if (!canDeleteNotification) return;
      if (!id) return console.error("Id is required to delete Notification");
      dispatch(setNotificationFetching(true));
      deleteNotificationWs(id);
    },
    [canDeleteNotification, dispatch, deleteNotificationWs]
  );
  const getProfileDataOnce = (force = false) => {
    if (
      force ||
      !profileData ||
      Object.keys(profileData).length === 0 ||
      globalThis.isFirstTime
    ) {
      dispatch(getProfileData()).then((action) =>
        middleware(action, "Profile Data", (action) => {
          setProfileData(action.payload);
          globalThis.profileData = action.payload;
          globalThis.isFirstTime = false;
        })
      );
    }
  };
  const balanceFetchedRef = useRef(false);
  const getBalanceDataOnce = useCallback(
    (force = false) => {
      // 1. Skip completely for Sellers and Managers
      if (isSeller(user) || isManager(user)) return;
      // 2. Avoid duplicate fetch if already cached and not forced
      if (
        !force &&
        balanceFetchedRef.current &&
        (newBalanceData || balanceData)
      ) {
        return;
      }
      balanceFetchedRef.current = true;
      // 3. Dispatch with user credentials
      dispatch(
        getBalanceData({
          userId: user?.id,
          username: user?.username || user?.userName || user?.systemId,
        })
      ).then((action) => {
        middleware(action, "Balance Data");
      });
    },
    [dispatch, user, newBalanceData, balanceData]
  );

  useEffect(() => {
    if (isSeller(user) || isManager(user)) return;
    // Polls every 30s across all pages
    const interval = setInterval(() => {
      getBalanceDataOnce(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [user, getBalanceDataOnce]);

  useEffect(() => {
    if (isSeller(user) || isManager(user)) return;
    getBalanceDataOnce();
  }, [user, getBalanceDataOnce]);

  const { clickRef, contRef, showModal, handleOpen, handleClose } = useModal();
  // useNotificationThrottle(5, 30, getNotificationDataOnce);

  useEffect(() => {
    const isDarkMode = localStorage.getItem("darkMode") === "true";
    setDarkMode(isDarkMode);
    applyTheme(isDarkMode);
  }, []);

  const initialSetupDone = useRef(false);
  const notificationSessionKey =
    user?.accessToken || user?.id || user?.username || user?.systemId || null;

  // Drop previous role's badge/list immediately on logout or account switch
  useEffect(() => {
    setNotificationCount(0);
    if (typeof globalThis !== "undefined") {
      globalThis.notificationCount = 0;
    }
    dispatch(clearStatenotification());
    initialSetupDone.current = false;
    // Keep panel in loading state until the new session's fetch returns
    if (notificationSessionKey) {
      dispatch(setNotificationFetching(true));
    }
  }, [notificationSessionKey, dispatch]);

  useEffect(() => {
    if (!notificationSocketConnected) return;
    if (initialSetupDone.current) return;
    getProfileDataOnce();
    getNotificationDataOnce();
    getBalanceDataOnce();
    initialSetupDone.current = true;
  }, [
    notificationSocketConnected,
    notificationSessionKey,
    getProfileDataOnce,
    getNotificationDataOnce,
    getBalanceDataOnce,
  ]);

  const userDataGetOnce = () => {
    dispatch(userListGet("editUser")).then((action) =>
      middleware(action, "User List", (action) => {
        setUserList(action.payload.usernames);
      })
    );
  };

  useEffect(() => {
    if (isSupers) userDataGetOnce();
  }, []);



  useEffect(() => {
    const unregister = registerEventHandler((event) => {
      if (event?.type === "session-reset") {
        setNotificationCount(0);
        if (typeof globalThis !== "undefined") {
          globalThis.notificationCount = 0;
        }
        dispatch(clearStatenotification());
        dispatch(setNotificationFetching(true));
        initialSetupDone.current = false;
        return;
      }

      if (event?.type === "fetch") {
        const socketNotifications = isArray(event.notifications) ? event.notifications : [];
        dispatch(setNotificationsFromSocket([...socketNotifications].reverse()));
        dispatch(setNotificationFetching(false));
        pendingMutationRef.current = false;

        const filteredNotifications = socketNotifications.filter(
          (element) =>
            element.title !== "Low Balance Alert" &&
            element.title !== "Add Balance Request Alert"
        );
        const totalUnread = filteredNotifications.filter((ntf) => !ntf.read).length;
        const latestTitle = filteredNotifications[0]?.title;

        if (latestTitle === "Profile Updated" && +globalThis.notificationCount !== totalUnread) {
          getProfileDataOnce(true);
        }
        if (latestTitle === "Low Balance Alert") {
          setNotifyId(filteredNotifications[0]?.id);
          setLowBalanceAlert(true);
        }
        if (latestTitle === "Add Balance Request Alert") {
          setAddBalanceLater(true);
          setAddBalanceId(filteredNotifications[0]);
        }

        setNotificationCount(totalUnread);
        if (globalThis) globalThis.notificationCount = totalUnread;
        return;
      }

      if (event?.type === "success") {
        getNotificationDataOnce(true);
        return;
      }

      if (event?.type === "live") {
        if (pendingMutationRef.current) {
          return;
        }

        getNotificationDataOnce(true);
        return;
      }

      if (event?.type === "error") {
        dispatch(setNotificationFetching(false));
        pendingMutationRef.current = false;
        getNotificationDataOnce(true);
      }
    });

    return () => unregister();
  }, [dispatch, registerEventHandler, getProfileDataOnce, getNotificationDataOnce]);
  useEffect(() => {
    const handleRefreshWallet = () => getBalanceDataOnce(true);
    window.addEventListener("refreshWallet", handleRefreshWallet);
    return () =>
      window.removeEventListener("refreshWallet", handleRefreshWallet);
  }, [getBalanceDataOnce]);

  useEffect(() => {
    const handleOpenNotification = () => handleNotificationModalOpen();
    const handleOpenFileProgress = () => handleFileProgressModalOpen();
    const handleOpenSubmissionReport = () => handleSubmissionReportOpen();
    const handleOpenCustomizeFilter = (e) => handleCustomizeFilter(e);

    window.addEventListener("openNotificationModal", handleOpenNotification);
    window.addEventListener("openDownloadProgressModal", handleOpenFileProgress);
    window.addEventListener("openSubmissionReportModal", handleOpenSubmissionReport);
    window.addEventListener("openCustomizePanelModal", handleOpenCustomizeFilter);

    return () => {
      window.removeEventListener("openNotificationModal", handleOpenNotification);
      window.removeEventListener("openDownloadProgressModal", handleOpenFileProgress);
      window.removeEventListener("openSubmissionReportModal", handleOpenSubmissionReport);
      window.removeEventListener("openCustomizePanelModal", handleOpenCustomizeFilter);
    };
  }, [
    canShowNotificationsNav,
    canShowDownloadsNav,
    canShowSubmissionReportNav,
    canShowCustomizeNav,
  ]);

  useEffect(() => {
    if (isSuccessBalance && newBalanceData) {
      setBalanceData({
        wallets: newBalanceData.walletBalance,
        credits: newBalanceData.credits,
        currency: newBalanceData.currency,
        walletFlag:
          newBalanceData.walletType?.toLowerCase() === "wallet" ? "yes" : "no",
        companyBalance: isBalanceFieldPresent(newBalanceData.companyBalance)
          ? newBalanceData.companyBalance
          : null,
      });
    }
  }, [isSuccessBalance, newBalanceData]);

  const goToWallet = () => navigate("/wallet");
  const personalCreditsAmount = formatBalanceAmount(balanceData?.credits);
  const personalWalletAmount = formatBalanceAmount(balanceData?.wallets);
  const personalWalletCurrency = balanceData?.currency || "";
  const walletFlagYes = checkForWalletMin(balanceData?.walletFlag);
  const companyBalance = balanceData?.companyBalance;
  const hasCompanyBalance =
    companyBalance !== null &&
    companyBalance !== undefined &&
    typeof companyBalance === "object";
  const companyName =
    (hasCompanyBalance && companyBalance.companyName) ||
    t("companyBalance.label");
  const companyWalletCurrency =
    personalWalletCurrency &&
      String(personalWalletCurrency).toUpperCase() !== "CRD"
      ? personalWalletCurrency
      : "";
  const showCompanyCredits =
    hasCompanyBalance &&
    isBalanceFieldPresent(companyBalance.companyCreditsBalance) &&
    (canSeeDualBalance || !walletFlagYes);
  const showCompanyWallet =
    hasCompanyBalance &&
    isBalanceFieldPresent(companyBalance.companyWalletBalance) &&
    (canSeeDualBalance || walletFlagYes);
  const showCompanyChips = showCompanyCredits || showCompanyWallet;
  const renderWalletIcon = () => (
    <AppIcon name="crdEur" size={18} alt="" />
  );
  const renderCompanyIcon = () => (
    <HiOfficeBuilding
      className="company-balance-icon"
      size={20}
      aria-hidden="true"
    />
  );

  return (
    <>
      <header>
        <nav>
          {hasRoles("admin,tenant", user) &&
            canShowCustomizeNav && (
            <div
              className="navItem hide"
              data-tooltip={t("tooltips.customizePanel")}
              data-tooltip-location="bottom"
            >
              <button onClick={handleCustomizeFilter}>
                <img src={cutomiseIcon} alt="" loading="lazy" />
              </button>
            </div>
          )}
          {canAccessDefault("theme") && (
          <div
            className="navItem navItem--theme-toggle"
            data-tooltip={
              darkMode ? t("tooltips.lightMode") : t("tooltips.darkMode")
            }
            data-tooltip-location="bottom"
            onClick={toggleDarkMode}
          >
            <button type="button">
              <AppIcon name="darkLightMode" size={20} alt="" />
            </button>
          </div>
          )}
          {canShowNotificationsNav && (
          <div
            className="navItem position-relative"
            data-tooltip={t("tooltips.notifications")}
            data-tooltip-location="bottom"
            onClick={handleNotificationModalOpen}
          >
            <button>
              <AppIcon name="notification" size={20} alt="" />
            </button>
            {notificationCount !== 0 && (
              <span
                style={{
                  right: -8,
                  top: -8,
                  backgroundColor: "tomato",
                }}
                className="position-absolute badge  rounded-circle"
              >
                {notificationCount}
              </span>
            )}
          </div>
          )}
          {!isSeller(user) && !isManager(user) && (
            <>
              {canShowQuickMessageNav && (
              <div
                className="navItem hide"
                onClick={handleQuickMessage}
                data-tooltip={t("tooltips.quickMessage")}
                data-tooltip-location="bottom"
              >
                <button>
                  <AppIcon name="quickMessage" size={20} alt="" />
                </button>
              </div>
              )}
              {canShowBulkMessageNav && (
              <div
                className="navItem hide"
                onClick={handleBulkMessage}
                data-tooltip={t("tooltips.bulkMessage")}
                data-tooltip-location="bottom"
              >
                <button>
                  <AppIcon name="bulkMessage" size={20} alt="" />
                </button>
              </div>
              )}
              {canShowSubmissionReportNav && (
              <div
                className="navItem hide"
                onClick={handleSubmissionReportOpen}
                data-tooltip={t("tooltips.submissionReport")}
                data-tooltip-location="bottom"
              >
                <button>
                  <AppIcon name="submissionReport" size={20} alt="" />
                </button>
              </div>
              )}
            </>
          )}
          {canShowDownloadsNav && (
          <div
            className="navItem"
            data-tooltip={t("tooltips.fileDownloadProgress")}
            data-tooltip-location="bottom"
            onClick={handleFileProgressModalOpen}
          >
            <button>
              <AppIcon name="fileDownloadProgress" size={20} alt="" />
            </button>
          </div>
          )}
          {canAccessDefault("language") && (
          <div
            className={
              "navItem language-selector" +
              (isMobile ? " language-selector--compact" : "")
            }
            title={isMobile ? languageLabels[selectedFlag] : undefined}
          >
            <ReactFlagsSelect
              countries={["US", "IN", "SA", "RU"]}
              customLabels={languageLabels}
              selected={selectedFlag}
              onSelect={handleLanguageChange}
              showOptionLabel={true}
              showSelectedLabel={!isMobile}
              alignOptionsToRight={isMobile}
              selectedSize={14}
              optionsSize={14}
              placeholder="Select Language"
            />
            {/* Dark-mode-safe caret (library caret can become low-contrast) */}
            <span
              className={
                "language-selector-caret" +
                (isMobile ? " language-selector-caret--compact" : "")
              }
              aria-hidden="true"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
          )}
          {!isSeller(user) && !isManager(user) && canAccessWalletUi && (
            <div className="navbar-balances">
              {canSeeDualBalance ? (
                <>
                  <BalanceChip
                    tooltip={`${personalCreditsAmount} CRD`}
                    onClick={goToWallet}
                    icon={renderWalletIcon()}
                    amount={personalCreditsAmount}
                    currency="CRD"
                  />
                  <BalanceChip
                    tooltip={`${personalWalletAmount} ${personalWalletCurrency}`.trim()}
                    onClick={goToWallet}
                    icon={renderWalletIcon()}
                    amount={personalWalletAmount}
                    currency={personalWalletCurrency}
                  />
                </>
              ) : (
                <BalanceChip
                  tooltip={
                    canSeeDualBalance
                      ? `Credits: ${personalCreditsAmount} CRD | Wallet: ${personalWalletAmount} ${personalWalletCurrency}`.trim()
                      : walletFlagYes
                      ? `${personalWalletAmount} ${personalWalletCurrency}`.trim()
                      : `${personalCreditsAmount} CRD`
                  }
                  onClick={goToWallet}
                  icon={renderWalletIcon()}
                  amount={
                    walletFlagYes
                      ? personalWalletAmount
                      : personalCreditsAmount
                  }
                  currency={walletFlagYes ? personalWalletCurrency : "CRD"}
                />
              )}
              {showCompanyChips && !isMobile && (
                <>
                  <div
                    className="navbar-balances-divider"
                    aria-hidden="true"
                  />
                  {showCompanyCredits && (
                    <BalanceChip
                      tooltip={t("companyBalance.creditsTooltip", {
                        name: companyName,
                        amount: formatBalanceAmount(
                          companyBalance.companyCreditsBalance
                        ),
                      })}
                      icon={renderCompanyIcon()}
                      amount={formatBalanceAmount(
                        companyBalance.companyCreditsBalance
                      )}
                      currency="CRD"
                    />
                  )}
                  {showCompanyWallet && (
                    <BalanceChip
                      tooltip={t("companyBalance.walletTooltip", {
                        name: companyName,
                        amount: formatBalanceAmount(
                          companyBalance.companyWalletBalance
                        ),
                        currency: companyWalletCurrency
                          ? ` ${companyWalletCurrency}`
                          : "",
                      })}
                      icon={renderCompanyIcon()}
                      amount={formatBalanceAmount(
                        companyBalance.companyWalletBalance
                      )}
                      currency={companyWalletCurrency}
                    />
                  )}
                </>
              )}
            </div>
          )}
          <div className="navItem more" ref={clickRef}>
            <button onClick={handleOpen}>
              <BsThreeDotsVertical color="#3f4d70" size={20} />
            </button>
          </div>
        </nav>

        {showModal && (
          <div className="moreOptions" ref={contRef}>
            {hasRoles("admin,tenant", user) && canShowCustomizeNav && (
              <div
                className="navItem hide"
                onClick={(e) => {
                  handleClose();
                  handleCustomizeFilter(e);
                }}
              >
                <button type="button">
                  <img src={cutomiseIcon} alt="" loading="lazy" />
                  <span>{t("menu.customizePanel")}</span>
                </button>
              </div>
            )}
            {!isSeller(user) && !isManager(user) && (
              <>
                {canShowQuickMessageNav && (
                <div
                  className="navItem hide"
                  data-tooltip={t("tooltips.quickMessage")}
                  data-tooltip-location="left"
                  onClick={(e) => {
                    handleClose();
                    handleQuickMessage(e);
                  }}
                >
                  <button type="button">
                    <AppIcon name="quickMessage" size={18} alt="" />
                    <span>{t("tooltips.quickMessage")}</span>
                  </button>
                </div>
                )}
                {canShowBulkMessageNav && (
                <div
                  className="navItem hide"
                  data-tooltip={t("tooltips.bulkMessage")}
                  data-tooltip-location="left"
                  onClick={(e) => {
                    handleClose();
                    handleBulkMessage(e);
                  }}
                >
                  <button type="button">
                    <AppIcon name="bulkMessage" size={18} alt="" />
                    <span>{t("tooltips.bulkMessage")}</span>
                  </button>
                </div>
                )}
                {canShowSubmissionReportNav && (
                <div
                  className="navItem hide"
                  data-tooltip={t("tooltips.submissionReport")}
                  data-tooltip-location="left"
                  onClick={(e) => {
                    handleClose();
                    handleSubmissionReportOpen(e);
                  }}
                >
                  <button type="button">
                    <AppIcon name="submissionReport" size={18} alt="" />
                    <span>{t("tooltips.submissionReport")}</span>
                  </button>
                </div>
                )}
              </>
            )}
          </div>
        )}

      </header>
      {canShowNotificationsNav && showNotification && (
        <NotificationModal
          isLoading={isFetchingNotification}
          close={() => setShowNotification(false)}
          show={showNotification}
          notificationData={notificationData}
          deleteNotificationOnce={deleteNotificationOnce}
          handleNotificationClick={handleNotificationClick}
          clearAllNotifications={handleClearAllNotifications}
          canClearAll={canClearAllNotifications}
          canDelete={canDeleteNotification}
          canMarkRead={canMarkNotificationRead}
        />
      )}
      {canShowDownloadsNav && showFileProgress && (
        <DownloadProgressModal
          close={() => setShowFileProgress(false)}
          show={showFileProgress}
        />
      )}

      {hasRoles("admin,tenant", user) && canShowCustomizeNav && showCustomizePanel && (
        <CustomizePanelFilter
          modal={showCustomizePanel}
          toggleModal={handleCustomizeFilter}
        />
      )}

      {hasRoles("admin,tenant,user", user) && lowBalanceAlert && (
        <LowBalanceAlertModal
          profileData={profileData}
          modal={lowBalanceAlert}
          toggleModal={() => setLowBalanceAlert(false)}
          deleteNotificationOnce={deleteNotificationOnce}
          notifyId={notifyId}
          userList={userList}
          handleNotificationClick={handleNotificationClick}
          balanceData={balanceData}
        />
      )}
      {hasRoles("superadmin,admin,tenant", user) && (
        <>
          {addBalanceLater && (
            <AddBalanceRequestModal
              modal={addBalanceLater}
              toggleModal={() => setAddBalanceLater(false)}
              profileData={profileData}
              deleteNotificationOnce={deleteNotificationOnce}
              addBalanceId={addBalanceId}
              setBalanceUser={setBalanceUser}
              balanceUser={balanceUser}
              lowBalanceNotification={notificationData.filter(
                (no) => no.title === "Add Balance Request Alert"
              )}
            />
          )}
        </>
      )}

      {!hasRoles("seller,manager", user) && (
        <>
          {canShowSubmissionReportNav && showSubmissionReport && (
            <SubmissionReport
              modal={showSubmissionReport}
              toggleModal={() => setShowSubmissionReport(false)}
            />
          )}
        </>
      )}
    </>
  );
};

export default Navbar;
